/**
 * Normalized org or project row from the coordinator (or any compatible source).
 */
export type OrgProjectContextOption = {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  createdAt?: string;
  /**
   * Whether the current user can edit this org/project (set by the backend).
   * Not always present — when it isn't true, treat it as "cannot edit".
   */
  canEdit?: boolean;
  /**
   * Whether the current user can invite members to this project (set by the
   * backend). Not always present — when it isn't true, treat it as "cannot
   * invite".
   */
  canInvite?: boolean;
};

/** IDs from the current URL (may be partial or wrong). */
export type RequestedOrgProjectContext = {
  organizationId?: string;
  projectId?: string;
};

/**
 * Loads org/project lists. Implementations own HTTP, caching, and auth.
 */
export type OrgProjectLoader = {
  listOrganizations(): Promise<OrgProjectContextOption[]>;
  listProjects(organizationId: string): Promise<OrgProjectContextOption[]>;
};

export type OrgProjectFallbackContext = {
  organizationId: string;
  projectId: string;
};

export type OrgProjectInvalidReason =
  | "missing_project_id"
  | "unknown_organization_id"
  | "unknown_project_id";

/**
 * Result of resolving URL org/project against loaded lists.
 * `fallbackContext` is a suggested default pair when the URL is wrong or incomplete.
 */
export type OrgProjectResolution =
  | {
      outcome: "empty";
      organizations: OrgProjectContextOption[];
      projects: OrgProjectContextOption[];
      fallbackContext: null;
    }
  | {
      outcome: "redirect";
      organizationId: string;
      projectId: string;
      organizations: OrgProjectContextOption[];
      projects: OrgProjectContextOption[];
      fallbackContext: OrgProjectFallbackContext;
    }
  | {
      outcome: "valid";
      organizationId: string;
      projectId: string;
      organizations: OrgProjectContextOption[];
      projects: OrgProjectContextOption[];
      fallbackContext: null;
    }
  | {
      outcome: "invalid";
      reason: OrgProjectInvalidReason;
      organizations: OrgProjectContextOption[];
      projects: OrgProjectContextOption[];
      fallbackContext: OrgProjectFallbackContext | null;
    };

export class OrgProjectRouteError extends Error {
  result: OrgProjectResolution;
  constructor(result: OrgProjectResolution) {
    super(
      result.outcome === "empty"
        ? "No accessible organizations or projects"
        : "The requested organization or project was not found"
    );
    this.name = "OrgProjectRouteError";
    this.result = result;
  }
}
